<template>
  <!-- Company Settings Section -->
  <div class="section-container pb-2">
    <div
      class="section-title d-flex align-center px-6 py-3 subtitle-1 font-weight-medium"
    >
      <v-icon color="#FF6B35" class="mr-2">mdi-cog</v-icon>
      <span>{{ $t("company_settings") }}</span>
    </div>
    <v-divider></v-divider>

    <div class="pa-5 d-flex" style="gap: 20px" v-if="model?.contract !== 4">
      <div class="flex-grow-1 d-flex flex-column">
        <label class="caption font-weight-bold grey--text text--darken-2 mb-2">
          {{ $t("active") }}
        </label>
        <v-switch
          v-model="model.state"
          :false-value="0"
          :true-value="1"
          color="success"
          dense
          hide-details
          class="mt-0 pt-0"
        />
      </div>
      <div class="flex-grow-1 d-flex flex-column">
        <label class="caption font-weight-bold grey--text text--darken-2 mb-2">
          {{ $t("credit_only") }}
        </label>
        <v-switch
          v-model="model.is_credit"
          :false-value="0"
          :true-value="1"
          color="warning"
          dense
          hide-details
          class="mt-0 pt-0"
        />
      </div>
      <div class="flex-grow-1 d-flex flex-column">
        <label class="caption font-weight-bold grey--text text--darken-2 mb-2">
          {{ $t("can_see_report_taken_meals") }}
        </label>
        <v-switch
          v-model="model.checkReport"
          :false-value="0"
          :true-value="1"
          color="info"
          dense
          hide-details
          class="mt-0 pt-0"
        />
      </div>
      <div class="flex-grow-1 d-flex flex-column" v-if="showTimeLimit">
        <label class="caption font-weight-bold grey--text text--darken-2 mb-2">
          {{ $t("no_time_limit") }}
        </label>
        <v-switch
          v-model="model.no_time_limit"
          :false-value="0"
          :true-value="1"
          color="info"
          dense
          hide-details
          class="mt-0 pt-0"
        />
      </div>
    </div>
    <div class="pa-5 d-flex" style="gap: 20px">
      <div class="flex-grow-1">
        <text-field
          v-model="model.name"
          :rules="[rules.required]"
          required
          :label="$t('title')"
        />
      </div>
    </div>

    <div class="px-5 d-flex border-top" style="gap: 20px" v-if="parentCompanies?.length">
      <div class="flex-grow-1">
        <autocomplete
          v-model="model.parent"
          :items="parentCompanies"
          :label="$t('parent_company')"
          item-text="name"
          return-object
          clearable
        />
      </div>
    </div>

    <div
      v-if="model?.contract !== 4"
      class="pa-5 d-flex border-top"
      style="gap: 20px"
    >
      <div class="flex-grow-1">
        <v-checkbox
          v-model="model.everyday_offer"
          :false-value="0"
          :true-value="1"
          @change="handleEveryDayOfferChange"
          color="#FF6B35"
          dense
          hide-details
        >
          <template v-slot:label>
            <span class="caption font-weight-medium grey--text text--darken-2">
              {{ $t("everyday_offer") }}
            </span>
          </template>
        </v-checkbox>
      </div>
    </div>

    <div
      class="px-5 d-flex border-top"
      style="gap: 20px"
      v-if="model?.contract !== 4"
    >
      <div class="flex-grow-1">
        <autocomplete
          v-model="model.type"
          :items="menuTypes"
          :label="$t('available_menu_type_for_comp')"
          item-text="filteredName"
          item-value="name"
          required
          :rules="[rules.required]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel.js";
import hasValidation from "@/mixins/hasValidation.js";
import hasSnackbar from "@/mixins/hasSnackbar.js";
import menuService from "@/services/menu";
import companyService from "@/services/company";

export default {
  name: "CompanySettings",
  mixins: [hasModel, hasValidation, hasSnackbar],
  props: {
    companies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      menuTypes: [],
    };
  },
  computed: {
    showTimeLimit() {
      return this.model.contract === 0 || this.restaurant?.settings?.no_time_limit;
    },
    parentCompanies() {
      // Return empty array if companies not available, or filter for parent companies
      return (this.companies?.filter((item) => item.id) || []).map((item) => ({
        ...item,
        name: this.toLatin(item.name),
        company: this.toLatin(item.company),
      }));
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
  },
  created() {
    this.loadMenuTypes();
  },
  methods: {
    async loadMenuTypes() {
      try {
        const menuTypesResponse = await menuService.getMenuTypes();
        this.menuTypes = menuTypesResponse.data.map((type) => ({
          ...type,
          filteredName: this.$t(type.name) || type.name,
        }));
      } catch (error) {
        console.error("Error loading menu types:", error);
        this.showErrorSnackbar(this.$t("error_loading_menu_types"));
      }
    },
    async handleEveryDayOfferChange() {
      if (!this.model.id) return;

      try {
        await companyService.changeEveryDayOffer(this.model);
        this.showSuccessSnackbar(this.$t("everyday_offer_updated"));
      } catch (error) {
        console.error("Error updating everyday offer:", error);
        this.showErrorSnackbar(this.$t("error_updating_everyday_offer"));
      }
    },
  },
};
</script>
